import './App.css';
import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProductsProvider } from './context/ProductsContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { ToastProvider } from './context/ToastContext';
import { ShippingSettingsProvider } from './context/ShippingSettingsContext';
import Header from './components/Header';
import Footer from './components/Footer';
import FreeShippingBanner from './components/FreeShippingBanner';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import PageLoader from './components/PageLoader';
import { warmBackend } from './config/api';

const Home = lazy(() => import('./components/Home'));
const Login = lazy(() => import('./components/Login'));
const Signup = lazy(() => import('./components/Signup'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Products = lazy(() => import('./pages/Products'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const ShippingInfo = lazy(() => import('./pages/ShippingInfo'));
const Returns = lazy(() => import('./pages/Returns'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const CheckoutWrapper = lazy(() => import('./components/checkout/CheckoutWrapper'));
const ShippingStepPage = lazy(() => import('./pages/checkout/ShippingStepPage'));
const DeliveryStepPage = lazy(() => import('./pages/checkout/DeliveryStepPage'));
const PaymentStepPage = lazy(() => import('./pages/checkout/PaymentStepPage'));
const ReviewStepPage = lazy(() => import('./pages/checkout/ReviewStepPage'));
const CheckoutReturn = lazy(() => import('./pages/CheckoutReturn'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));
const MyOrders = lazy(() => import('./pages/MyOrders'));
const MyAddresses = lazy(() => import('./pages/MyAddresses'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminShippingSettings = lazy(() => import('./pages/admin/AdminShippingSettings'));

// Conditional Layout Wrapper
function ConditionalLayout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Fixed header/banner offsets: measure actual rendered heights and store in CSS vars.
  // This avoids overlap across fonts, zoom levels, and bootstrap spacing utilities.
  useLayoutEffect(() => {
    const hasHeader = !isAdminRoute;
    document.body.classList.toggle('has-ecomm-header', hasHeader);
    document.body.classList.toggle('has-banner', hasHeader);

    if (!hasHeader) return;

    const apply = () => {
      const banner = document.querySelector('.free-shipping-banner');
      const nav = document.querySelector('.ah-navbar');
      const bannerH = banner ? Math.round(banner.getBoundingClientRect().height) : 0;
      const navH = nav ? Math.round(nav.getBoundingClientRect().height) : 0;

      document.documentElement.style.setProperty('--ah-banner-height', `${bannerH}px`);
      if (navH) document.documentElement.style.setProperty('--ah-header-height', `${navH}px`);
    };

    apply();

    // Keep vars accurate across breakpoint switches (e.g. device toolbar) and dynamic header layout changes.
    let raf = 0;
    const scheduleApply = () => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => apply());
    };

    window.addEventListener('resize', scheduleApply);

    const banner = document.querySelector('.free-shipping-banner');
    const nav = document.querySelector('.ah-navbar');
    let ro = null;
    if (typeof window.ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => scheduleApply());
      if (banner) ro.observe(banner);
      if (nav) ro.observe(nav);
    }

    // Re-apply after fonts/layout settle.
    const t = window.setTimeout(apply, 50);

    return () => {
      window.removeEventListener('resize', scheduleApply);
      window.clearTimeout(t);
      if (raf) window.cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
    };
  }, [isAdminRoute]);

  // Ensure classes are cleaned if React unmounts layout.
  useEffect(() => {
    return () => {
      document.body.classList.remove('has-ecomm-header');
      document.body.classList.remove('has-banner');
    };
  }, []);

  return (
    <div className="app-root d-flex flex-column min-vh-100">
      {!isAdminRoute && <FreeShippingBanner />}
      {!isAdminRoute && <Header />}
      <main className="flex-fill">
        {children}
      </main>
      {!isAdminRoute && <Footer />}
    </div>
  );
}

function App() {
  // Warm backend (e.g. Render) on first load so cold start happens early
  useEffect(() => {
    warmBackend();
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <WishlistProvider>
          <ProductsProvider>
            <ToastProvider>
              <ErrorBoundary>
                <Router>
                  <ShippingSettingsProvider>
                  <ConditionalLayout>
                    <Suspense fallback={<PageLoader text="Loading page…" />}>
                    <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <UserProfile />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/products" element={<Products />} />
                  <Route path="/product/:productId" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute>
                        <CheckoutWrapper />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/checkout/shipping" replace />} />
                    <Route path="shipping" element={<ShippingStepPage />} />
                    <Route path="delivery" element={<DeliveryStepPage />} />
                    <Route path="payment" element={<PaymentStepPage />} />
                    <Route path="review" element={<ReviewStepPage />} />
                  </Route>
                  <Route
                    path="/checkout/return"
                    element={
                      <ProtectedRoute>
                        <CheckoutReturn />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/order-success/:orderId"
                    element={
                      <ProtectedRoute>
                        <OrderSuccess />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-orders"
                    element={
                      <ProtectedRoute>
                        <MyOrders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-orders/:orderId"
                    element={
                      <ProtectedRoute>
                        <MyOrders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-addresses"
                    element={
                      <ProtectedRoute>
                        <MyAddresses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wishlist"
                    element={<Wishlist />}
                  />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/shipping-info" element={<ShippingInfo />} />
                  <Route path="/returns" element={<Returns />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <AdminLayout>
                        <AdminDashboard />
                      </AdminLayout>
                    }
                  />
                  <Route
                    path="/admin/products"
                    element={
                      <AdminLayout>
                        <AdminProducts />
                      </AdminLayout>
                    }
                  />
                  <Route
                    path="/admin/categories"
                    element={
                      <AdminLayout>
                        <AdminCategories />
                      </AdminLayout>
                    }
                  />
                  <Route
                    path="/admin/orders"
                    element={
                      <AdminLayout>
                        <AdminOrders />
                      </AdminLayout>
                    }
                  />
                  <Route
                    path="/admin/reviews"
                    element={
                      <AdminLayout>
                        <AdminReviews />
                      </AdminLayout>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <AdminLayout>
                        <AdminUsers />
                      </AdminLayout>
                    }
                  />
                  <Route
                    path="/admin/coupons"
                    element={
                      <AdminLayout>
                        <AdminCoupons />
                      </AdminLayout>
                    }
                  />
                  <Route
                    path="/admin/settings"
                    element={
                      <AdminLayout>
                        <AdminShippingSettings />
                      </AdminLayout>
                    }
                  />
                  <Route
                    path="/admin/profile"
                    element={
                      <AdminLayout>
                        <AdminProfile />
                      </AdminLayout>
                    }
                  />
                    </Routes>
                    </Suspense>
                  </ConditionalLayout>
                  </ShippingSettingsProvider>
                </Router>
              </ErrorBoundary>
            </ToastProvider>
          </ProductsProvider>
        </WishlistProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
